import type {
	AuthErrorCode,
	LoginCommand,
	RegisterCommand,
	SessionResponse,
} from '@sirius/contracts';
import { TERMS_VERSION } from '@sirius/contracts';

import { ApiError, request } from '../client';

/**
 * Автентифікація. Бекенд — Better Auth, змонтований під `/api/auth/*`.
 *
 * Клієнтську бібліотеку Better Auth свідомо НЕ використовуємо: вона стала б
 * другим шляхом доступу до мережі повз `client.ts`, а §5 гайду вимагає рівно
 * одну точку входу. Ендпоінти — звичайний JSON, тож обгортка нічого не коштує.
 *
 * Через межу їде КОД помилки, а не текст: тексти зі специфікації живуть
 * у features/auth/schemas.ts, бо це копірайт інтерфейсу.
 */

export class AuthError extends Error {
	constructor(readonly code: AuthErrorCode) {
		super(code);
		this.name = 'AuthError';
	}
}

/** Коди, які ми вміємо показати. Решта зводиться до UNKNOWN. */
const KNOWN_CODES = new Set<string>([
	'EMAIL_ALREADY_EXISTS',
	'PHONE_NUMBER_ALREADY_EXISTS',
	'INVALID_EMAIL_OR_PASSWORD',
	'EMAIL_NOT_VERIFIED',
	'ACCOUNT_BLOCKED',
	'ACCOUNT_INACTIVE',
	'INVALID_TOKEN',
	'TERMS_NOT_ACCEPTED',
	'DESTINATION_REQUIRED',
	'DESTINATION_UNAVAILABLE',
]);

/** Мережа — тільки через request(). Тут лише переклад ApiError у код форми. */
async function post<T>(path: string, body: unknown): Promise<T> {
	try {
		return await request<T>(`/auth${path}`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(body),
		});
	} catch (error) {
		const code = error instanceof ApiError ? error.code : undefined;
		throw new AuthError(
			code !== undefined && KNOWN_CODES.has(code) ? (code as AuthErrorCode) : 'UNKNOWN',
		);
	}
}

/**
 * Реєстрація. `name` бекенду потрібне як єдине поле, а спека збирає імʼя
 * з двох окремих — складаємо його тут, щоб форма про це не думала.
 */
export async function register(command: RegisterCommand): Promise<void> {
	await post('/sign-up/email', {
		email: command.email,
		password: command.password,
		name: `${command.firstName} ${command.lastName}`.trim(),
		firstName: command.firstName,
		lastName: command.lastName,
		phoneCountryCode: command.phone.countryCode,
		phoneNumber: command.phone.number,
		// Порожнє поле форми — «компанії немає», а не порожня назва: інакше адмінка показала б дилера без імені.
		companyName: command.companyName?.trim() || null,
		// Без вибору поле не шлемо: з одним портом бекенд поставить дилера в системний.
		destinationPort: command.destinationPort || undefined,
		// Редакція, яку дилер щойно бачив у модалці; без прапорця форма сюди не доходить.
		termsVersion: command.acceptedTerms ? TERMS_VERSION : undefined,
	});
}

export async function login(command: LoginCommand): Promise<void> {
	await post('/sign-in/email', {
		email: command.email,
		password: command.password,
		rememberMe: command.rememberMe,
	});
}

/**
 * Запит на скидання пароля. Відповідь однакова незалежно від того, чи існує
 * акаунт — так вимагає спека, і саме так поводиться бекенд.
 */
export async function requestPasswordReset(email: string): Promise<void> {
	await post('/request-password-reset', { email });
}

export async function resetPassword(token: string, newPassword: string): Promise<void> {
	await post('/reset-password', { token, newPassword });
}

/** Повторна відправка листа підтвердження — кнопка Resend на Verify Email. */
export async function resendVerificationEmail(email: string): Promise<void> {
	await post('/send-verification-email', { email });
}

export async function logout(): Promise<void> {
	await post('/sign-out', {});
}

/**
 * Поточна сесія. `null` означає «не автентифікований», а не помилку.
 *
 * Питаємо власний `/api/me`, а не `/api/auth/get-session`: той віддає
 * внутрішню форму Better Auth і не знає нашого словника статусів, тому
 * заблокованому після входу показував би, що він усередині акаунта.
 */
export const getSession = (): Promise<SessionResponse | null> =>
	request<SessionResponse | null>('/me');
